import React, { useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCopy, FiDownload, FiExternalLink, FiFileText, FiGift } from 'react-icons/fi';
import { generateStandardCommercialGestureTransferFiles } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import {
    parsePolicyCodes,
    ResultDetails,
} from './bulkTransfers/StandardEndOfStayTransferPanel';
import {
    useCommercialGesturePermissions,
} from './commercialGestures/CommercialGestureShared';
import './commercialGestures/commercialGestures.css';

const FileLink = ({ label, link, totalRecords }) => {
    const [copied, setCopied] = useState(false);
    if (!link) return null;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
                <span className="small fw-semibold">{label}</span>
                {totalRecords !== undefined && (
                    <span className="text-muted small">
                        ({totalRecords} enregistrement{totalRecords > 1 ? 's' : ''})
                    </span>
                )}
            </div>
            <div className="d-flex align-items-start gap-2 flex-wrap">
                <code className="small flex-grow-1" style={{ wordBreak: 'break-all' }}>{link}</code>
                <Button variant="outline-secondary" size="sm" onClick={copy}>
                    <FiCopy className="me-1" />
                    {copied ? 'Copié !' : 'Copier'}
                </Button>
                {String(link).startsWith('http') && (
                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-decoration-none small">
                        <FiExternalLink className="me-1" />
                        Ouvrir
                    </a>
                )}
            </div>
        </div>
    );
};

const FileDownloadSection = ({ data }) => {
    if (!data) return null;
    const files = Array.isArray(data.files) ? data.files : [];
    const csvFile = files.find((file) => file.type === 'CSV');
    const excelFile = files.find((file) => file.type === 'Excel');
    if (!data.s3_console_url && !csvFile && !excelFile) return null;

    return (
        <div className="mt-3">
            <strong>Fichiers disponibles :</strong>
            {data.s3_console_url && (
                <div className="mt-2">
                    <FileLink label="Dossier S3" link={data.s3_console_url} />
                </div>
            )}
            {csvFile?.s3_console_url && (
                <FileLink
                    label="Fichier banque (CSV)"
                    link={csvFile.s3_console_url}
                    totalRecords={csvFile.total_records}
                />
            )}
            {excelFile?.s3_console_url && (
                <FileLink
                    label="Import paid daily (Excel)"
                    link={excelFile.s3_console_url}
                    totalRecords={excelFile.total_records}
                />
            )}
        </div>
    );
};

const AdminCommercialGestureBulkTransfers = () => {
    const navigate = useNavigate();
    const { canWrite } = useCommercialGesturePermissions();
    const [policyCodesInput, setPolicyCodesInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const inFlightRef = useRef(false);

    const policyCodes = parsePolicyCodes(policyCodesInput);

    const handleGenerate = async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const result = await generateStandardCommercialGestureTransferFiles(policyCodes);
            setData(result);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de la génération des fichiers de virement'));
        } finally {
            inFlightRef.current = false;
            setLoading(false);
        }
    };

    if (!canWrite) {
        return (
            <div className="dashboard-page admin-commercial-gestures-page">
                <Alert variant="danger">Action non autorisée</Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-commercial-gestures-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <span className="page-title-icon" aria-hidden="true">
                                    <FiGift />
                                </span>
                                Fichiers de virement — Gestes commerciaux
                            </h2>
                            <p className="text-muted mb-0">
                                Génère le CSV de virements et l&apos;Excel d&apos;import paid daily
                                (montant = montant du geste). Aucun statut n&apos;est modifié.
                            </p>
                        </div>
                        <Button
                            variant="outline-secondary"
                            onClick={() => navigate('/admin/trips/commercial-gestures')}
                        >
                            <FiArrowLeft className="me-2" />
                            Retour
                        </Button>
                    </div>

                    <Alert variant="light" className="border mb-3">
                        <strong>Éligibilité :</strong> geste au statut « Lien RIB envoyé » ; police non
                        annulée ; RIB reçu ; montant &gt; 0 ; LCB-FT sans sanction en cours ni PEP,
                        sauf LCB-FT vérifié ou distribution inclusion.
                    </Alert>

                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FiFileText className="me-2" />
                                Codes de police (optionnel)
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group controlId="cg-bulk-policy-codes">
                                <Form.Label>Filtrer par codes de police</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={policyCodesInput}
                                    onChange={(e) => setPolicyCodesInput(e.target.value)}
                                    placeholder="Ex. 658204658, 123456789"
                                    disabled={loading}
                                />
                                <Form.Text className="text-muted">
                                    Séparez les codes par des virgules, espaces ou retours à la ligne.
                                    {policyCodes.length > 0
                                        ? ` ${policyCodes.length} code(s) détecté(s).`
                                        : ' Sans filtre, tous les gestes éligibles du périmètre Standard.'}
                                </Form.Text>
                            </Form.Group>

                            <Button
                                variant="primary"
                                className="mt-3"
                                onClick={handleGenerate}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Génération en cours...
                                    </>
                                ) : (
                                    <>
                                        <FiDownload className="me-2" />
                                        Générer les fichiers
                                    </>
                                )}
                            </Button>
                        </Card.Body>
                    </Card>

                    {error && <Alert variant="danger">{error}</Alert>}
                    <ResultDetails
                        data={data}
                        renderFiles={(result) => <FileDownloadSection data={result} />}
                    />
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminCommercialGestureBulkTransfers;
